import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge } from '@evogard/ui';
import type {
  CoreEntity,
  ErpHealth,
  Breaker,
  ClassificationState,
  MirroredEntity,
  RefreshCounts,
  StatusDomain,
  SyncResult,
} from '@evogard/contract';
import { formatInstant, loadFailureMessage, type PaymentSource } from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { api, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
// registra os avisos de tempo real do ERP — `common.tsx` é importado por toda tela do módulo
import './live.js';

/**
 * O vocabulário do espelho do ERP, num lugar só.
 *
 * A saúde da integração aparece em três lugares — a tela, o painel do início e
 * a tela de classificação, que mostra o estado do espelho de situações. Repetir
 * o rótulo do disjuntor ou o "atualizado há 2h" em cada uma é como o mesmo
 * estado vira "aberto" numa tela e "indisponível" na outra.
 *
 * As **consultas** também moram aqui, e por um motivo mais estreito do que a
 * economia de linhas: a chave é a mesma nos três lugares, então o painel e a
 * tela compartilham uma leitura só de `GET /erp/health` em vez de baterem duas
 * vezes numa rota que fala com uma integração limitada a uma chamada a cada
 * quatro segundos. Chave escrita à mão em cada arquivo diverge no dia em que
 * alguém acrescenta um filtro — e o sintoma é a tela mostrando um número e o
 * painel logo acima mostrando outro.
 */

/* ------------------------------------------------------------------------- *
 * As consultas
 * ------------------------------------------------------------------------- */

/**
 * A saúde da integração **na base atual**.
 *
 * `tenantId` na chave porque a resposta é da praça da sessão: sem ele, trocar
 * de base deixaria a tela nomeando a base nova sobre os números da anterior —
 * e nesta tela, cuja frase é "a sincronização da praça X", é o defeito mais
 * provável de todos.
 *
 * `placeholderData` segura o resultado anterior só **dentro da mesma base**,
 * pela mesma razão: entre bases, o certo é o esqueleto voltar.
 *
 * `refetchInterval` é de quem pede, e só a tela de saúde pede: o worker parado
 * não manda aviso nenhum, e o andamento das esteiras também não (ver
 * `ERP_PIPELINE_EVENT`), então é a releitura que faz a tela perceber. O painel
 * do início lê a mesma chave e não precisa repetir.
 */
export function useErpHealth(
  refetchInterval?: (health: ErpHealth | undefined) => number,
) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery<ErpHealth>({
    queryKey: ['erp', 'saude', tenantId],
    queryFn: async () => {
      const r = await api.erp.health();
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a saúde da integração'));
      }
      return r.body;
    },
    placeholderData: (previous, queryPrevious) =>
      queryPrevious?.queryKey[2] === tenantId ? previous : undefined,
    ...(refetchInterval === undefined
      ? {}
      : { refetchInterval: (query) => refetchInterval(query.state.data) }),
  });
}

/**
 * A classificação de situações da base atual — a lista, o que falta classificar
 * e o estado do espelho que diria o que existe.
 *
 * A tela de saúde também a consome, e não é desperdício: o estado que domina
 * aquela tela hoje é a classificação vazia, e dizer isso **sem ler** seria a
 * tela afirmando o que o código não sabe. É outra pergunta, não a mesma
 * consulta duas vezes.
 */
export function useClassificationState(includeArchived = false) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['erp', 'classificacoes', tenantId, includeArchived],
    queryFn: async () => {
      const r = await api.erp.listClassifications({
        // `'true'`/`'false'` e não booleano: na query string tudo é texto, e o
        // contrato lê o texto justamente para `'false'` querer dizer falso
        query: { includeArchived: includeArchived ? 'true' : 'false' },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a classificação de situações'));
      }
      return r.body;
    },
    placeholderData: (previous, queryPrevious) =>
      queryPrevious?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/* ------------------------------------------------------------------------- *
 * Como a casa nomeia o que vem do espelho
 * ------------------------------------------------------------------------- */

/**
 * O nome de cada entidade espelhada, na língua do negócio.
 *
 * `Record` completo e não `Partial`: entidade nova no contrato não compila até
 * ganhar rótulo aqui, e o caminho silencioso — a tela escrevendo
 * `situacao_veiculo` cru numa coluna — deixa de existir.
 */
// @valor
export const ENTITY_LABEL: Record<MirroredEntity, string> = {
  associado: 'Associados',
  veiculo: 'Veículos',
  boleto: 'Boletos',
  voluntario: 'Voluntários',
  venda: 'Vendas',
  situacao_boleto: 'Situações de boleto',
  situacao_veiculo: 'Situações de veículo',
  situacao_motivo: 'Motivos de situação',
  referencia: 'Tabelas de referência',
  veiculo_produto: 'Produtos dos veículos',
  atendimento: 'Atendimentos',
  alteracao: 'Alterações',
  associado_detalhe: 'Associados (ficha completa)',
};

/** O nome de cada entidade do NÚCLEO — a lista da projeção, que não é a do espelho. */
// @valor
export const CORE_ENTITY_LABEL: Record<CoreEntity, string> = {
  associado: 'Associados',
  voluntario: 'Voluntários',
  veiculo: 'Veículos',
  boleto: 'Boletos',
  boleto_veiculo: 'Boletos por veículo',
  veiculo_produto: 'Produtos dos veículos',
  referencia: 'Tabelas de referência',
  situacao_veiculo: 'Situações de veículo',
  situacao_boleto: 'Situações de boleto',
  situacao_motivo: 'Motivos de situação',
  atendimento: 'Atendimentos',
  alteracao: 'Alterações',
};

/* ------------------------------------------------------------------------- *
 * O disjuntor
 * ------------------------------------------------------------------------- */

type Tone = 'sucesso' | 'erro' | 'alerta' | 'neutro' | 'informacao';

/**
 * Os quatro estados do disjuntor, ditos por extenso.
 *
 * **`sem_falhas` não é "nunca falhou", e o rótulo diz isso.** A janela
 * deslizante é podada, então depois da poda ninguém consegue responder "nunca"
 * — um selo escrito "nunca falhou" sabendo apenas "não falhou nos últimos dez
 * minutos" seria a tela afirmando o que o código não cumpre.
 *
 * E `fechado` é separado de `sem_falhas` de propósito: os dois passam chamadas,
 * mas só um deles é uma praça a quatro falhas de parar. Com um rótulo só, a
 * diferença sumiria justamente no dia em que ela importa.
 */
// @valor
const STATES: Record<Breaker['estado'], { label: string; tone: Tone }> = {
  sem_falhas: { label: 'Sem falhas na janela', tone: 'sucesso' },
  fechado: { label: 'Passando, com falhas recentes', tone: 'alerta' },
  meio_aberto: { label: 'Testando o retorno', tone: 'alerta' },
  aberto: { label: 'Aberto — recusando chamadas', tone: 'erro' },
};

export function BreakerBadge({ estado }: { estado: Breaker['estado'] }) {
  const e = STATES[estado];
  return <Badge tone={e.tone}>{e.label}</Badge>;
}

/**
 * A frase abaixo do selo — com os números que o servidor mandou, e só eles.
 *
 * O limite e o tamanho da janela vêm da resposta, nunca escritos aqui: o
 * contrato os manda justamente para a tela não guardar a segunda cópia de um
 * número que alguém vai calibrar um dia.
 */
export function breakerExplanation(breaker: Breaker): string {
  const window = `${breaker.windowMinutes} min`;
  switch (breaker.estado) {
    case 'sem_falhas':
      return (
        `Nenhuma falha nos últimos ${window}. ` +
        'Não quer dizer que nunca falhou: a janela só guarda esse período.'
      );
    case 'fechado':
      return (
        `${breaker.failuresNaWindow} de ${breaker.failuresLimit} falhas nos últimos ${window}. ` +
        'As chamadas continuam saindo; ao chegar no limite, o disjuntor abre.'
      );
    case 'meio_aberto':
      return 'A espera venceu. A próxima chamada mede se o ERP voltou.';
    case 'aberto':
      return (
        `${breaker.failuresNaWindow} falhas nos últimos ${window} abriram o disjuntor. ` +
        'Os pedidos ficam de pé e saem quando ele fechar.'
      );
  }
}

/*
 * Reexportado, e não redeclarado: os dois moram em `components/Values.tsx`
 * porque dois módulos precisavam deles e a saída fácil tinha sido copiar.
 */
export { Age } from '../../components/Values.js';

/* -------------------------------------------------------------------------- *
 * O cadastro espelhado — `erp-37` a `erp-39`
 * -------------------------------------------------------------------------- */

/*
 * O selo da situação mora em `components/StatusBadge.tsx` desde 20/10/2026:
 * a mesa de sinistros mostra a situação do associado com ele, e a fronteira
 * de módulo não deixa outro módulo importar daqui.
 */
export { StatusBadge } from '../../components/StatusBadge.js';

/**
 * O valor pago, com a PROCEDÊNCIA dele ao lado.
 *
 * `app.boletos` guarda dois pagamentos de propósito, e eles respondem coisas
 * diferentes: `paid_at`/`paid_amount` é a baixa da COLETA — o gateway, que
 * ainda não existe — e `erp_paid_at`/`erp_paid_amount` é o que o SGA diz.
 * Medido em 05/10/2026: a coleta tem **3.198** linhas de 1.249.074, e o ERP
 * considera pagos **852.348**. Uma tela que lesse só a primeira mostraria
 * "sem pagamento" em quase todo boleto quitado do cadastro.
 *
 * A diferença não é curiosidade: "o banco confirmou" e "o sistema do
 * fornecedor registrou" é a diferença entre poder e não poder cobrar de novo.
 * Por isso o rótulo aparece junto do número, e não num rodapé de tabela.
 *
 * ## E o rótulo da esquerda diz menos do que o nome dele promete
 *
 * `coleta` **não** quer dizer "entrou pela nossa conta", e o texto do `title`
 * foi escrito para não dizer isso. A projeção semeia `paid_at` no nascimento da
 * linha com o `data_pagamento` do próprio ERP, e nas 3.198 linhas da CE
 * `paid_at = erp_paid_at` em todas as 3.198 — nenhuma é baixa de gateway,
 * porque gateway não há. O que ele responde hoje é "a casa tem registro próprio
 * da baixa". Ver `boletoPayment` em `@evogard/domain`, seção "`coleta` não
 * prova canal HOJE".
 */
export function PaidValue({
  value,
  source,
}: {
  value: string | null;
  /* o tipo do domínio, e não uma união escrita à mão: uma terceira procedência
     — o gateway, quando ele existir — tem que quebrar esta tela, não passar */
  source: PaymentSource | null;
}) {
  if (value === null) return <MoneyValue value={null} />;
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <MoneyValue value={value} />
      {source !== null && (
        <span
          className="text-xs text-texto-suave"
          title={
            source === 'coleta'
              ? 'a casa tem registro próprio desta baixa — não necessariamente confirmação bancária nossa'
              : 'só o sistema de gestão registrou este pagamento; a casa não tem baixa própria'
          }
        >
          {source === 'coleta' ? 'coleta' : 'ERP'}
        </span>
      )}
    </span>
  );
}

/**
 * Quando o espelho foi lido pela última vez.
 *
 * Regra 8 na tela: quem consulta declara o frescor que precisa, e esta tela
 * precisa de `usage: 'tela'` — aceita o espelho **e diz a idade dele**. Sem o
 * carimbo, a tela afirmaria o cadastro de hoje sobre uma varredura de ontem.
 */
export function MirrorStamp({ at }: { at: string | null }) {
  if (at === null) {
    return <span className="text-texto-suave">sem data de sincronização</span>;
  }
  return (
    <span className="text-texto-suave">espelho sincronizado em {formatInstant(at)}</span>
  );
}

/**
 * Os códigos de situação que a praça conhece — classificados ou não.
 *
 * As duas telas do cadastro precisam dele para o filtro ser uma lista e não um
 * campo de digitar, e duas cópias divergiriam no dia em que uma passasse a
 * incluir os arquivados. Os não classificados vêm primeiro porque são eles que
 * trazem a descrição do fornecedor; as classificadas trazem a do vocabulário
 * do núcleo, que a API junta. A lista sai em ordem de NOME, que é como o filtro
 * a mostra.
 */
export function knownCodes(
  state: ClassificationState | null | undefined,
  domain: StatusDomain,
): Array<{ code: string; descricao: string | null }> {
  if (!state) return [];

  const seen = new Map<string, string | null>();
  for (const c of state.notClassified) {
    if (c.domain === domain) seen.set(c.code, c.descricao);
  }
  for (const c of state.classifications) {
    if (c.domain === domain && !seen.has(c.code)) seen.set(c.code, c.descricao);
  }
  return [...seen.entries()]
    .map(([code, descricao]) => ({ code, descricao }))
    .sort((a, b) =>
      (a.descricao ?? a.code).localeCompare(b.descricao ?? b.code, 'pt-BR', {
        numeric: true,
      }),
    );
}

/* ------------------------------------------------------------------------- *
 * O "Atualizar do ERP" — o mesmo botão em toda ficha
 * ------------------------------------------------------------------------- */

/** O que se pede ao ERP: a entidade, a chave dela lá, e por qual campo. */
export type RefreshTarget = {
  entity: 'associado' | 'veiculo' | 'boleto';
  id: string;
  by?: 'codigo' | 'placa';
} | null;

/** Por quanto tempo a ficha se relê depois de um `em_andamento` — o job continua no worker. */
const FOLLOW_MS = 180_000;

/**
 * O pedido ao ERP e o que a ficha faz depois dele — desde 14/10/2026.
 *
 * A rota ESPERA o job: `200` quer dizer que o espelho foi gravado e o núcleo
 * projetado, e aí basta reler a ficha. `202 em_andamento` quer dizer que a
 * espera acabou antes do job — ele continua, e cada etapa dele já projeta o
 * que gravou —, então a ficha se relê de três em três segundos por até três
 * minutos (`follow`), em vez de a pessoa recarregar no escuro.
 *
 * `queryKey` é a da ficha: é ela que é invalidada no fim e relida enquanto o
 * job continua.
 */
export function useErpRefresh(queryKey: readonly unknown[], target: () => RefreshTarget) {
  const client = useQueryClient();
  const [followUntil, setFollowUntil] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async (): Promise<SyncResult> => {
      const body = target();
      if (body === null) {
        throw new Error('este registro não tem código no ERP — não há a quem pedir');
      }
      const r = await api.erp.sync({ body });
      if (r.status === 200 || r.status === 202) return r.body;
      throw new Error(errorMessage(r.body, 'O ERP não respondeu ao pedido.'));
    },
    onSuccess: (result) => {
      setFollowUntil(result.estado === 'em_andamento' ? Date.now() + FOLLOW_MS : null);
      /* todas as leituras do ERP: a ficha, as listas e a do associado de um carro */
      void client.invalidateQueries({ queryKey: ['erp'] });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey });
    },
  });

  /** O `refetchInterval` da ficha: três segundos enquanto o job continua. */
  const follow = () =>
    followUntil !== null && Date.now() < followUntil ? 3_000 : (false as const);

  return { mutation, follow };
}

/** A frase do que veio — "1 veículo, 4 produtos e 63 boletos". */
function countsSentence(counts: RefreshCounts): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(counts.members, 'associado', 'associados');
  add(counts.vehicles, 'veículo', 'veículos');
  add(counts.products, 'produto', 'produtos');
  add(counts.boletos, 'boleto', 'boletos');
  add(counts.serviceRecords, 'atendimento', 'atendimentos');
  if (parts.length === 0) return 'o registro';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

/**
 * O que o pedido respondeu, dito como é. Cinco desfechos, e nenhum vira o
 * outro: "atualizado" só com o job terminado, "em andamento" com ele ainda
 * rodando, e a falha com o motivo do ERP.
 */
export function RefreshOutcome({
  mutation,
}: {
  mutation: ReturnType<typeof useErpRefresh>['mutation'];
}) {
  if (mutation.isPending) {
    return (
      <Alert tone="informacao">
        Buscando no ERP agora — o registro, o que vem com ele e o histórico de boletos. Um
        carro antigo leva alguns segundos; a ficha se atualiza aqui mesmo.
      </Alert>
    );
  }
  if (mutation.isError) return <Alert tone="erro">{mutation.error.message}</Alert>;
  const result = mutation.data;
  if (!result) return null;

  if (result.estado === 'atualizado') {
    return (
      <Alert tone={result.warnings.length > 0 ? 'alerta' : 'sucesso'}>
        Atualizado do ERP: {countsSentence(result.counts)}
        {result.counts.boletoWindows > 1 &&
          ` (${result.counts.boletosListed} listado(s) em ${result.counts.boletoWindows} janelas de 200 dias)`}
        . A ficha abaixo já é a nova.
        {result.warnings.length > 0 && (
          <ul className="mt-2 list-disc pl-5">
            {result.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }
  if (result.estado === 'inexistente') {
    return (
      <Alert tone="alerta">
        O ERP respondeu que não tem este registro. A ficha mostra o que a casa tinha.
      </Alert>
    );
  }
  if (result.estado === 'em_andamento') {
    return (
      <Alert tone="informacao">
        O ERP ainda está respondendo — o histórico deste registro é longo. O que já chegou
        está na ficha, e ela continua se atualizando sozinha por alguns minutos.
      </Alert>
    );
  }
  return (
    <Alert tone="alerta">
      O ERP está fora do ar para esta base, e nada foi pedido. Tente de novo depois de{' '}
      {formatInstant(result.until)}.
    </Alert>
  );
}
